import {
  MONTAJ_OLCUM_HATLARI,
  MONTAJ_OLCUM_TIPLERI,
  montajOlcuCoz,
  type MontajAkisBilgisi,
  type MontajBirimDurumu,
  type MontajBirimYaniti,
  type MontajKaydiYaniti,
  type MontajOlcumHatti,
  type MontajOlcumTipi,
  type MontajOlcumYaniti,
  type MontajOnerisi,
  type MontajRaporUyumu,
  type MontajRiserYaniti,
  type MontajRol,
  type MontajSatirDurumu,
  type MontajSatirYaniti,
} from "@oasis/domain";
import { api } from "@/lib/api";
import { cevir } from "@/lib/dil";
import { listeSatiriMi, satirEtiketleri, type EtiketGorunumu } from "@/lib/montaj";

/**
 * D-068: ekranda doldurulan FM-0012 formunun (MontajForm.tsx; riserın Assembly sayfasında kayıt türü `form`) saf mantığı.
 * Ekran yalnız çizer ve çağırır: birim yuvaları, alan güncelleme gövdeleri, öneri çiplerinin durumu, doluluk ve son kutunun hâli
 * burada. JSX yok; React yüklemez.
 *
 * İlke: başka bir kayıttan gelen hiçbir kimlik forma kendiliğinden yazılmaz. Riser kaydı, OEM kitabı, söküm kaydı ve stok seçimi
 * sunucudan ÖNERİ olarak gelir; değer ancak kişi "Use" (ya da "Use all") ile aldığında ya da kendisi yazdığında alan
 * güncellemesiyle kayda girer. Print-out kapısının kuralı sunucunundur (`kayit.formEksikleri`); burada yeniden hesaplanmaz.
 */

/* ------------------------------------------------------- uçlar ve gövde ------------------------------------------------------- */

/** Formun sunucu uçları (`/api` önekini `lib/api.ts` ekler). İmzalı çıktının dosyası var olan Finishing ucuna gider (`MONTAJ_UCU.asmRapor`). */
export const MONTAJ_FORM_UCU = {
  /** POST: riserda ekran formunu başlatır ("Fill the form on screen"); yanıt `{ id }` */
  ac: (rn: string) => `/montaj/riser/${rn}/form`,
  /** PATCH: alan alan güncelleme (`MontajFormGovdesi`); yanıt sayfanın tam durumu (`MontajRiserYaniti`) */
  guncelle: (id: string) => `/montaj/${id}`,
  /** POST: "Generate print-out" (yalnız işaret yazar; sistem çıktı üretmez) */
  yazdir: (id: string) => `/montaj/${id}/print`,
  /** POST: kaydı Finishing'e yüklenmiş imzalı çıktıya bağlar ve kapatır; yinelenebilir */
  imzali: (id: string) => `/montaj/${id}/imzali`,
} as const;

/** Birimin kimlik alanları, ekrandaki sabit sırayla. */
export type BirimAlani = "sn" | "pn" | "heat" | "po" | "wo" | "etiket" | "diger";
export const BIRIM_ALANLARI: readonly BirimAlani[] = ["sn", "pn", "heat", "po", "wo", "etiket", "diger"];
/** Alanların kısa adları; ilk beşi çevrilmez. */
export const ALAN_ADI: Readonly<Record<BirimAlani, string>> = { sn: "SN", pn: "PN", heat: "Heat", po: "PO", wo: "WO", etiket: cevir("Tag"), diger: cevir("Other") };

/** Gövdedeki birim: yalnız dolu alanlar gider (gönderilmeyen alan sunucuda boşalır). */
export type BirimYamasi = Partial<Record<BirimAlani | "not", string>> & { rol?: MontajRol; durum?: MontajBirimDurumu };
/** Satırın gövdesi. `birimler` verilirse satırın birimlerinin TAMAMININ yerini alır. */
export interface SatirYamasi {
  durum?: MontajSatirDurumu | null;
  /** basılı adetten farklı TOPLAM adet (30. satırda takılan modül sayısı); null = basılı adet geçerli */
  qty?: number | null;
  fittedBy?: string | null;
  not?: string | null;
  birimler?: BirimYamasi[];
}
export type OnaySatiri = "tamamlama" | "yonetici" | "kalite";
/** Onay bloğunun üç satırı, formun basılı adlarıyla (çevrilmez). */
export const ONAY_SATIRLARI: readonly (readonly [OnaySatiri, string])[] = [["tamamlama", "Assembly Completion"], ["yonetici", "Manager Review"], ["kalite", "QA/QC Review"]];
export interface OlcumYamasi {
  tip: MontajOlcumTipi;
  hat: MontajOlcumHatti;
  /** inç; null ölçüyü siler */
  deger: number | null;
  /** kesirli girdinin yazıldığı gibi hâli ("3-7/16"); ondalık girdide null */
  yazim: string | null;
}
/** `PATCH /api/montaj/:id` gövdesi. Gönderilmeyen alan değişmez; null gönderilen alan boşaltılır. */
export interface MontajFormGovdesi {
  assemblySnYazim?: string | null;
  onemliNot?: string | null;
  yorum?: string | null;
  /** tarih "YYYY-AA-GG" ya da null */
  onay?: Partial<Record<OnaySatiri, { ad?: string | null; tarih?: string | null }>>;
  olcumler?: OlcumYamasi[];
  /** anahtar satır numarasıdır (1–30) */
  satirlar?: Record<string, SatirYamasi>;
}

/** Sunucunun gövdede kabul ettiği en çok uzunluklar (girdilerin `maxLength` değeri; sunucudaki sayılarla aynı tutulur). */
export const FORM_ALAN_SINIRI = {
  assemblySn: 100, sn: 100, pn: 100, heat: 100, po: 100, wo: 100, etiket: 100, diger: 200, birimNotu: 500, onemliNot: 2000, yorum: 2000, fittedBy: 200, satirNotu: 1000, onayAdi: 200, olcuYazimi: 100,
} as const;
/** Liste satırlarında (cıvata, pul, locking insert, buoyancy) girilebilen en çok birim (sunucunun sınırı). */
export const LISTE_BIRIM_SINIRI = 12;
/** Ölçü sayısı: iki grup, dört hat. */
export const FORM_OLCU_SAYISI = MONTAJ_OLCUM_TIPLERI.length * MONTAJ_OLCUM_HATLARI.length;

/** Tek satırın gövdesi. */
export const satirGovdesi = (sira: number, yama: SatirYamasi): MontajFormGovdesi => ({ satirlar: { [String(sira)]: yama } });

/** Onay bloğunun tek satırının gövdesi (ad ya da tarih). */
export function onayGovdesi(satir: OnaySatiri, ek: { ad?: string | null; tarih?: string | null }): MontajFormGovdesi {
  const onay: NonNullable<MontajFormGovdesi["onay"]> = {};
  onay[satir] = ek;
  return { onay };
}

/* ---------------------------------------------------- satırlar: alanlar ve adet ---------------------------------------------------- */

/** Ekranda seçilebilen satır durumları. `listede` (kâğıttaki "SEE BACK") yoktur: listeler satırın altında birim birim girilir. */
export type FormSatirDurumu = Exclude<MontajSatirDurumu, "listede">;
export const FORM_DURUMLARI: readonly FormSatirDurumu[] = ["takildi", "sahada", "takilmadi", "uygulanmaz"];

/** Parça başına kimlik taşıyan satırlar (1–11 ve 30): adet kadar birim yuvası açılır. Öteki satırlarda tek parti kimliği girilir. */
export const PARCA_BASINA: ReadonlySet<number> = new Set([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 30]);
/** Choke / Kill rolü seçilen satırlar: C&K hatları (3) ve C&K ayar kovanları (6). */
export const ROL_SATIRLARI: readonly number[] = [3, 6];

/** Birim bloğunda kendiliğinden görünen alanlar, satıra göre (sabit sırada). Kalanını "+ more fields" açar. */
export function birimAlanlari(sira: number): BirimAlani[] {
  if (sira === 1 || sira === 3 || sira === 4 || sira === 5) return ["sn"];
  if (sira === 2) return ["pn", "heat", "po", "wo", "etiket", "diger"];
  if (sira === 6 || sira === 7) return ["pn", "heat", "po", "etiket", "diger"];
  if (sira === 8) return ["sn", "pn", "heat", "etiket", "diger"];
  if (sira === 9 || sira === 10) return ["etiket", "diger"];
  if (sira === 11) return ["sn", "pn", "heat", "po", "etiket", "diger"];
  if (sira >= 12 && sira <= 19) return ["pn", "po", "diger"];
  if (sira >= 20 && sira <= 29) return ["po", "diger"];
  // Buoyancy modülünün kimliği bütün olarak tek alandadır (okunan kayıtlarla aynı kolon).
  if (sira === 30) return ["diger"];
  return [];
}

/**
 * Birim bloğunda çizilen alanlar: varsayılanlar ve dolu olanlar (dolu alan her zaman görünür); `hepsi` ise yedisi. Sabit sırada.
 * `kayittaki`: aynı birimin kayıttaki hâli; kayıtta dolu olan alan, kişi içini silerken de görünür kalır.
 */
export function gorunenAlanlar(
  sira: number,
  birim: Partial<Record<BirimAlani, string | null>>,
  hepsi: boolean,
  kayittaki?: Partial<Record<BirimAlani, string | null>> | null,
): BirimAlani[] {
  if (hepsi) return [...BIRIM_ALANLARI];
  const varsayilan = birimAlanlari(sira);
  return BIRIM_ALANLARI.filter((a) => varsayilan.includes(a) || Boolean(birim[a]?.trim()) || Boolean(kayittaki?.[a]?.trim()));
}

/** Satırın geçerli adedi: yazılan adet, yoksa basılı adet, ikisi de yoksa sıfır (30. satırda basılı adet yoktur). */
export const etkinAdet = (x: { qty: number | null; basiliQty: number | null }): number => x.qty ?? x.basiliQty ?? 0;

/**
 * Satırda açılan birim yuvası sayısı: parça başına kimlik taşıyan satırlarda adet kadar (liste satırlarında en çok
 * `LISTE_BIRIM_SINIRI`), öteki satırlarda adet kaç olursa olsun bir.
 */
export function birimSayisi(sira: number, adet: number): number {
  if (!PARCA_BASINA.has(sira)) return 1;
  const n = Number.isFinite(adet) && adet > 0 ? Math.floor(adet) : 0;
  return listeSatiriMi(sira) ? Math.min(n, LISTE_BIRIM_SINIRI) : n;
}

/** Adet girdisi: boş → null (basılı adet geçerli); 0–99 arası tam sayı (sunucunun sınırı); başka her yazım hata. */
export function adetOku(metin: string): { deger: number | null; hata: boolean } {
  const t = metin.trim();
  if (!t) return { deger: null, hata: false };
  return /^\d{1,2}$/.test(t) ? { deger: Number(t), hata: false } : { deger: null, hata: true };
}

/** Birim yuvaları bu durumda açık mı: liste satırlarında her durumda (durum seçilmemişken de), öteki satırlarda yalnız "fitted"de. */
export const birimlerAcik = (sira: number, durum: string | null): boolean => listeSatiriMi(sira) || durum === "takildi";

/* ---------------------------------------------------------- birim yuvaları ---------------------------------------------------------- */

/**
 * Ekrandaki bir birim yuvası. Sunucu birimleri konumla değil sırayla tutar (`seq` 1'den, boşluksuz): yuvalar önce kayıttaki
 * birimler, sonra boş yuvalardır. `sn` kişinin yazdığı metindir (kayıtta temiz değerden farklıysa yazım, değilse temiz değer).
 */
export interface Yuva {
  rol: MontajRol | null;
  sn: string;
  pn: string;
  heat: string;
  po: string;
  wo: string;
  etiket: string;
  diger: string;
  /** birim notu ("+ more fields" altında) */
  not: string;
  /** birim durumu; ekranda girdisi yoktur, kayıttaki değer korunur */
  durum: MontajBirimDurumu | null;
}
const BOS_YUVA: Yuva = { rol: null, sn: "", pn: "", heat: "", po: "", wo: "", etiket: "", diger: "", not: "", durum: null };

/** Birimin SN alanında görünen metin: kişinin yazdığı gibi (kayıttaki yazım), yoksa temiz değer. */
const snMetni = (b: Pick<MontajBirimYaniti, "sn" | "yazim">): string => b.yazim?.trim() || b.sn?.trim() || "";

type KayitBirimi = Pick<MontajBirimYaniti, "seq" | "rol" | "yazim" | "durum" | "not" | BirimAlani>;

/** Kayıttaki birimlerden yuvalar: önce birimler `seq` sırasıyla, sonra `sayi`ya kadar boş yuvalar. Kayıttaki birim hiçbir zaman gizlenmez. */
export function yuvalariKur(birimler: readonly KayitBirimi[], sayi: number): readonly Yuva[] {
  const dolu = [...birimler].sort((a, b) => a.seq - b.seq).map((b): Yuva => ({
    rol: b.rol, sn: snMetni(b), pn: b.pn ?? "", heat: b.heat ?? "", po: b.po ?? "", wo: b.wo ?? "", etiket: b.etiket ?? "", diger: b.diger ?? "", not: b.not ?? "", durum: b.durum,
  }));
  return yuvalariTamamla(dolu, sayi);
}

/** Yuvaları `sayi`ya tamamlar (boş yuva ekler; var olanı atmaz). Eklenecek yuva yoksa aynı diziyi döner. */
export function yuvalariTamamla(yuvalar: readonly Yuva[], sayi: number): readonly Yuva[] {
  if (yuvalar.length >= sayi) return yuvalar;
  return [...yuvalar, ...Array.from({ length: sayi - yuvalar.length }, () => ({ ...BOS_YUVA }))];
}

/** Bir yuvanın alanlarını değiştirir (yeni dizi döner). */
export function yuvaDegistir(yuvalar: readonly Yuva[], i: number, ek: Partial<Yuva>): readonly Yuva[] {
  return yuvalar.map((y, n) => (n === i ? { ...y, ...ek } : y));
}

/** Bir yuvanın tek metin alanını (kimlik alanı ya da birim notu) değiştirir. */
export function yuvaAlanYaz(yuvalar: readonly Yuva[], i: number, alan: BirimAlani | "not", deger: string): readonly Yuva[] {
  const ek: Partial<Yuva> = {};
  ek[alan] = deger;
  return yuvaDegistir(yuvalar, i, ek);
}

/** En az bir kimlik alanı dolu mu (rol, not ve birim durumu kimlik sayılmaz). */
export const kimlikli = (b: Partial<Record<BirimAlani, string | null>>): boolean => BIRIM_ALANLARI.some((a) => Boolean(b[a]?.trim()));

/**
 * Yuvalarda henüz gönderilemeyen yazı var mı: kimliği boş bir yuvada seçilmiş rol ya da yazılmış not. Bunlar tek başına
 * gönderilmez (kimlik girilince onunla birlikte gider); o zamana kadar ekrandaki taslakta durur.
 */
export const bekleyenYaziVar = (yuvalar: readonly Yuva[]): boolean => yuvalar.some((y) => !kimlikli(y) && (Boolean(y.rol) || Boolean(y.not.trim())));

type YuvaGirdisi = Partial<Record<BirimAlani | "not", string | null>> & { rol?: MontajRol | null; durum?: MontajBirimDurumu | null };

/**
 * Satırın `birimler` gövdesi: yalnız en az bir kimlik alanı dolu yuvalar, ekrandaki sırayla; değerler kırpılır. Kimliği boş yuva
 * gönderilmez (sunucu kimliksiz birimi reddeder), o yuvanın rolü ve notu da gitmez. Bütün yuvalar boşsa boş dizi (satırın
 * birimleri silinir). Bir birimin bütün kimlik alanları silinirse o birim kayıttan düşer, arkasındakiler bir öne kayar.
 */
export function birimlerYamasi(yuvalar: readonly YuvaGirdisi[]): BirimYamasi[] {
  const yama: BirimYamasi[] = [];
  for (const y of yuvalar) {
    if (!kimlikli(y)) continue;
    const b: BirimYamasi = {};
    if (y.rol) b.rol = y.rol;
    for (const a of BIRIM_ALANLARI) {
      const v = y[a]?.trim();
      if (v) b[a] = v;
    }
    const not = y.not?.trim();
    if (not) b.not = not;
    if (y.durum) b.durum = y.durum;
    yama.push(b);
  }
  return yama;
}

/** İki birim gövdesi aynı mı (aynı ise istek gönderilmez). Alanlar `birimlerYamasi`nın yazdığı sabit sırada karşılaştırılır. */
export const birimlerAyni = (a: readonly BirimYamasi[], b: readonly BirimYamasi[]): boolean => JSON.stringify(a) === JSON.stringify(b);

/** Satırın kayıttaki birimlerinin gövdesi (ekrandaki taslakla karşılaştırmak için). */
export const kayittakiBirimler = (birimler: readonly KayitBirimi[]): BirimYamasi[] => birimlerYamasi(yuvalariKur(birimler, 0));

/* -------------------------------------------------------------- öneriler -------------------------------------------------------------- */

/** Buoyancy satırı: öneri hiç gösterilmez (takım riserdan risera değişir; öneri çoğu riserda yanlış olurdu). */
export const ONERISIZ_SATIR = 30;
/** "Use all" yalnız bu satırlarda çizilir: C&K ayar kovanları, cıvata, pul, locking insert. Ana hatların kimliği tek tek alınır. */
export const TOPLU_ONERI_SATIRLARI: readonly number[] = [6, 9, 10, 11];
export const topluOneriSatiriMi = (sira: number): boolean => TOPLU_ONERI_SATIRLARI.includes(sira);

const sade = (s: string | null | undefined): string => (s ?? "").trim().toUpperCase();

/**
 * Öneri çipinin durumu: alan boşsa "bos" (çipte "Use"), aynı değeri taşıyorsa "ayni" (gri "same as …"), başka değer taşıyorsa
 * "farkli" ("differs"; düğme yok, kişi isterse alanı elle değiştirir). Karşılaştırma baş ve son boşluk ile harf büyüklüğü dışında
 * birebirdir; SN alanında `mevcut` sunucunun temiz değeridir (`oneriMevcut`).
 */
export function oneriDurumu(oneri: { deger: string }, mevcut: string | null | undefined): "bos" | "ayni" | "farkli" {
  if (!sade(mevcut)) return "bos";
  return sade(mevcut) === sade(oneri.deger) ? "ayni" : "farkli";
}

const STOK_ONEKI = "stock selection ";

/** Çipte görünen kaynak adı (çevrilmiş). Stok seçiminde ad paket numarasını taşır. */
export function kaynakAdi(o: Pick<MontajOnerisi, "kaynak" | "kaynakAd">): string {
  switch (o.kaynak) {
    case "riser":
      return cevir("riser record");
    case "mrb":
      return o.kaynakAd === "MRB (OEM) · MT weldment" ? cevir("MRB (OEM) · MT weldment") : cevir("MRB (OEM)");
    case "sokum":
      return cevir("removed at Disassembly");
    case "stok":
      return o.kaynakAd.startsWith(STOK_ONEKI) ? cevir("stock selection {0}", o.kaynakAd.slice(STOK_ONEKI.length)) : cevir(o.kaynakAd);
    default:
      return cevir(o.kaynakAd);
  }
}

/** Aynı alana aynı değeri veren önerilerin tek çipi. */
export interface OneriGrubu {
  alan: MontajOnerisi["alan"];
  deger: string;
  /** söküm birimindeki rol (yalnız 3. ve 6. satırda gelebilir); "Use" birimin rolü boşsa onu da yazar */
  rol: MontajRol | null;
  /** çipte görünen kaynak adları (çevrilmiş), geliş sırasıyla */
  kaynaklar: string[];
  /** sunucunun verdiği kaynak adları (İngilizce): alınan değerin birim notuna bunlar yazılır */
  kaynakAdlari: string[];
}

/**
 * Bir alanın çipleri. `sira` null: başlıktaki Assembly Serial No. önerileri. Değilse o satırın `seq` sıralı öneri takımı (sunucunun
 * aynı birim için verdiği öneriler); takımın hangi YUVADA çizileceğini `oneriEslemesi` söyler (konum değil değer).
 * Aynı alana iki kaynak aynı değeri veriyorsa tek grupta birleşir (kaynak adları sırayla); farklı değerler ayrı gruptur, hiçbiri
 * öne alınmaz. Buoyancy satırı (30) için öneri dönmez.
 */
export function oneriGruplari(oneriler: readonly MontajOnerisi[], sira: number | null, seq: number | null): OneriGrubu[] {
  if (sira === ONERISIZ_SATIR) return [];
  const gruplar: OneriGrubu[] = [];
  for (const o of oneriler) {
    const buraya = sira === null ? o.alan === "assemblySn" : o.alan !== "assemblySn" && o.sira === sira && o.seq === seq;
    if (!buraya || !o.deger.trim()) continue;
    const g = gruplar.find((x) => x.alan === o.alan && sade(x.deger) === sade(o.deger));
    if (!g) {
      gruplar.push({ alan: o.alan, deger: o.deger.trim(), rol: o.rol, kaynaklar: [kaynakAdi(o)], kaynakAdlari: [o.kaynakAd] });
      continue;
    }
    if (!g.kaynakAdlari.includes(o.kaynakAd)) {
      g.kaynaklar.push(kaynakAdi(o));
      g.kaynakAdlari.push(o.kaynakAd);
    }
    g.rol ??= o.rol;
  }
  return gruplar;
}

/** Birim alanına giden öneri (başlıktaki Assembly Serial No. önerisi değil). */
const birimOnerisiMi = (o: MontajOnerisi): o is MontajOnerisi & { alan: BirimAlani } => o.alan !== "assemblySn";

/**
 * Satırın öneri takımlarını ekrandaki yuvalara eşler: sonucun i. öğesi, i. yuvada çipleri çizilecek takımın `seq`'idir; o yuvaya
 * düşen takım yoksa null. `yuvalar`: çipin karşılaştırdığı değerler (SN alanında `oneriMevcut`).
 *
 * Sunucu önerileri birim SIRASIYLA verir, ama kayıt birimleri konumla tutmaz: kimliği boş yuva kayda girmez, arkasındakiler bir
 * öne kayar; kişi de parçaları önerilen sırayla girmek zorunda değildir (listelerde sıra anlam taşımaz, C&K hatlarında rol
 * birimin alanıdır). Bu yüzden eşleme konumla değil DEĞERLE yapılır:
 * 1. Bir yuva bir takımın değerini taşıyorsa (aynı alanda; `oneriDurumu` "ayni") o takım o yuvanındır. Birden çok aday varsa en
 *    çok alanı tutan çift önce eşlenir; eşitlikte küçük `seq`, sonra öndeki yuva.
 * 2. Kalan takımlar, `seq` sırasıyla, kalan yuvalara öndekinden başlayarak düşer (hiçbir değer tutmuyorsa eşleme konumladır:
 *    1. takım 1. yuvaya). Değeri hiçbir takımı tutmayan dolu yuva böylece bir takımla karşılaşır ve çipi "differs" der.
 * Sonuç: satırda zaten duran değerin önerisi başka yuvada yeniden sunulmaz, sıra dışı girilmiş aynı takım "differs" demez ve
 * "Use all" aynı değeri iki kez yazmaz. Buoyancy satırı (30) için eşleme yoktur.
 */
export function oneriEslemesi(oneriler: readonly MontajOnerisi[], sira: number, yuvalar: readonly Partial<Record<BirimAlani, string | null>>[]): (number | null)[] {
  const esleme: (number | null)[] = yuvalar.map(() => null);
  if (sira === ONERISIZ_SATIR) return esleme;
  const satirin = oneriler.filter(birimOnerisiMi).filter((o) => o.sira === sira && Boolean(o.deger.trim()));
  const seqler = [...new Set(satirin.map((o) => o.seq))].sort((a, b) => a - b);
  const adaylar: { seq: number; yuva: number; puan: number }[] = [];
  for (const seq of seqler) {
    const takim = satirin.filter((o) => o.seq === seq);
    yuvalar.forEach((y, yuva) => {
      const puan = new Set(takim.filter((o) => oneriDurumu(o, y[o.alan]) === "ayni").map((o) => o.alan)).size;
      if (puan > 0) adaylar.push({ seq, yuva, puan });
    });
  }
  adaylar.sort((a, b) => b.puan - a.puan || a.seq - b.seq || a.yuva - b.yuva);
  const eslenen = new Set<number>();
  for (const a of adaylar) {
    if (eslenen.has(a.seq) || esleme[a.yuva] !== null) continue;
    esleme[a.yuva] = a.seq;
    eslenen.add(a.seq);
  }
  const kalan = seqler.filter((s) => !eslenen.has(s));
  for (let i = 0; i < esleme.length && kalan.length > 0; i++) {
    if (esleme[i] === null) esleme[i] = kalan.shift() ?? null;
  }
  return esleme;
}

/**
 * "Use" ile alınan değerin birim notu. Ekran metni DEĞİLDİR: kayda yazılan veridir, İngilizce saklanır ve çevrilmez (değerin
 * parçadan okunmadığı, öneriden alındığı kayıtta görünsün).
 */
export const oneriNotu = (kaynakAdlari: readonly string[]): string => `taken from the suggestion: ${kaynakAdlari.join(" · ")}`;

/**
 * Çipin karşılaştırdığı değer. SN alanında, yuvadaki metin kayıttakiyle aynıysa sunucunun TEMİZ değeridir (öneriler de temiz
 * gelir; "0000 156 587-1" yazılmış alan "156587-1" önerisiyle aynı sayılır); kişi alanı değiştirdiyse ya da öteki alanlarda
 * yuvadaki metin.
 */
export function oneriMevcut(alan: BirimAlani, yuva: Yuva, birim: Pick<MontajBirimYaniti, "sn" | "yazim"> | null | undefined): string {
  if (alan === "sn" && birim && yuva.sn.trim() === snMetni(birim)) return birim.sn ?? yuva.sn;
  return yuva[alan];
}

/**
 * Başlıktaki Assembly Serial No. çipinin karşılaştırdığı değer (birim alanlarındaki `oneriMevcut` ile aynı kural). Alanda
 * kayıttakinden başka bir metin duruyorsa (kişi yazıyor ya da yazdığı değerin kaydı tutmadı) O metindir; değilse kaydın temiz
 * değeri (öneriler de temiz gelir), o da yoksa yazımı. `taslak` null: alanda kaydedilmemiş metin yok. Böylece alanda duran değer
 * ile yanındaki çip çelişmez: kaydı tutmayan değer alanda dururken çip kayıttaki eski değere bakıp "same as …" demez.
 */
export function baslikOneriMevcut(taslak: string | null, k: Pick<MontajKaydiYaniti, "assemblySn" | "assemblySnYazim">): string | null {
  if (taslak !== null && taslak.trim() !== (k.assemblySnYazim ?? "").trim()) return taslak;
  return k.assemblySn ?? k.assemblySnYazim;
}

/**
 * "Use": öneriyi yuvanın BOŞ alanına yazar; birimin rolü boşsa önerinin rolünü, notu boşsa kaynağı da yazar. Alan doluysa (ya da
 * yuva yoksa) hiçbir şey değişmez ve aynı dizi döner.
 */
export function oneriUygula(yuvalar: readonly Yuva[], i: number, g: Pick<OneriGrubu, "alan" | "deger" | "rol" | "kaynakAdlari">): readonly Yuva[] {
  const y = yuvalar[i];
  if (!y || g.alan === "assemblySn" || y[g.alan].trim()) return yuvalar;
  const ek: Partial<Yuva> = {};
  ek[g.alan] = g.deger;
  if (!y.rol && g.rol) ek.rol = g.rol;
  if (!y.not.trim()) ek.not = oneriNotu(g.kaynakAdlari);
  return yuvaDegistir(yuvalar, i, ek);
}

/** "Use all" düğmesi çizilen kaynaklar: satırda (yalnız 6, 9, 10, 11) aynı kaynaktan en az iki öneri varsa; çipteki adıyla. */
export function topluOneriKaynaklari(sira: number, oneriler: readonly MontajOnerisi[]): { kaynak: MontajOnerisi["kaynak"]; ad: string }[] {
  if (!topluOneriSatiriMi(sira)) return [];
  const sayim = new Map<MontajOnerisi["kaynak"], { n: number; ad: string }>();
  for (const o of oneriler) {
    if (o.sira !== sira || o.alan === "assemblySn" || !o.deger.trim()) continue;
    const s = sayim.get(o.kaynak);
    if (s) s.n += 1;
    else sayim.set(o.kaynak, { n: 1, ad: kaynakAdi(o) });
  }
  return [...sayim].filter(([, s]) => s.n >= 2).map(([kaynak, s]) => ({ kaynak, ad: s.ad }));
}

type TopluBirim = { seq: number; rol?: MontajRol | null } & Partial<Record<BirimAlani | "not", string | null>>;
/** "Use all"un bir birime yazacakları: yalnız değişen alanlar. */
export type TopluOneriYamasi = { seq: number; rol?: MontajRol; not?: string } & Partial<Record<BirimAlani, string>>;

/**
 * "Use all": verilen kaynağın önerilerini satırın birimlerine dağıtır. YALNIZ boş alanları doldurur, yazılmış değeri ezmez; rol
 * yalnız boşsa, kaynak notu yalnız birimin notu boşsa yazılır. Başka kaynağın ve başka satırın önerileri karışmaz. Yalnız verilen
 * birimlere (ekrandaki yuvalara, sırasıyla) bakar; değişen birim başına bir öğe döner (`seq` yuvanın numarasıdır). "Use all"
 * çizilmeyen satırda boş dizi. Hangi önerinin hangi yuvaya gideceği çiplerle AYNI eşlemedir (`oneriEslemesi`): satırda zaten
 * duran değer başka yuvaya ikinci kez yazılmaz, yerine henüz alınmamış öneri gelir.
 */
export function topluOneriYamasi(satir: { sira: number; birimler: readonly TopluBirim[] }, oneriler: readonly MontajOnerisi[], kaynak: MontajOnerisi["kaynak"]): TopluOneriYamasi[] {
  if (!topluOneriSatiriMi(satir.sira)) return [];
  const esleme = oneriEslemesi(oneriler, satir.sira, satir.birimler);
  const yama: TopluOneriYamasi[] = [];
  satir.birimler.forEach((b, i) => {
    const takim = esleme[i];
    if (takim === null || takim === undefined) return;
    const ek: TopluOneriYamasi = { seq: b.seq };
    let alinan: string | null = null;
    for (const o of oneriler) {
      if (o.kaynak !== kaynak || o.sira !== satir.sira || o.seq !== takim || !birimOnerisiMi(o) || !o.deger.trim()) continue;
      if (b[o.alan]?.trim() || ek[o.alan] !== undefined) continue;
      ek[o.alan] = o.deger.trim();
      alinan ??= o.kaynakAd;
      if (!b.rol && !ek.rol && o.rol) ek.rol = o.rol;
    }
    if (alinan === null) return;
    if (!b.not?.trim()) ek.not = oneriNotu([alinan]);
    yama.push(ek);
  });
  return yama;
}

/** "Use all" düğmesindeki sayı: doldurulacak alan sayısı. */
export const topluOneriSayisi = (yama: readonly TopluOneriYamasi[]): number => yama.reduce((n, y) => n + BIRIM_ALANLARI.filter((a) => y[a] !== undefined).length, 0);

/** "Use all"un sonucunu yuvalara işler (yuva `seq - 1`). */
export function topluOneriUygula(yuvalar: readonly Yuva[], yama: readonly TopluOneriYamasi[]): readonly Yuva[] {
  return yuvalar.map((y, i) => {
    const ek = yama.find((x) => x.seq === i + 1);
    if (!ek) return y;
    const yeni: Yuva = { ...y };
    for (const a of BIRIM_ALANLARI) {
      const v = ek[a];
      if (v !== undefined) yeni[a] = v;
    }
    if (ek.rol) yeni.rol = ek.rol;
    if (ek.not !== undefined) yeni.not = ek.not;
    return yeni;
  });
}

/* ------------------------------------------------------ satır durumu ve uyarılar ------------------------------------------------------ */

/**
 * "fitted"den başka duruma geçerken satırın birimlerinde dolu alan varsa önce sorulur (kimlikler silinecek). Liste satırlarında
 * (9, 10, 11, 30) sorulmaz: orada kimlikler durum ne olursa olsun kalır.
 */
export function durumDegisimiSorar(satir: { sira: number; durum: string | null; birimler: readonly (Partial<Record<BirimAlani | "not", string | null>> & { rol?: string | null })[] }, yeni: string | null): boolean {
  if (listeSatiriMi(satir.sira) || satir.durum !== "takildi" || yeni === "takildi") return false;
  return satir.birimler.some((b) => kimlikli(b) || Boolean(b.rol) || Boolean(b.not?.trim()));
}

/**
 * Durum düğmesinin gövdesi. Satırın "Fitted By" alanı boşsa ve bir durum seçiliyorsa görünür varsayılan da gider. Liste dışı
 * satır "fitted"den çıkıyorsa birimleri AÇIKÇA boş diziyle silinir (görünmeyen kimlik kayıtta kalmasın); liste satırlarında
 * birimler gövdeye girmez.
 */
export function durumYamasi(satir: Pick<MontajSatirYaniti, "sira" | "durum" | "fittedBy"> & { birimler: readonly unknown[] }, yeni: FormSatirDurumu | null, fittedByVarsayilan: string): SatirYamasi {
  const yama: SatirYamasi = { durum: yeni };
  const varsayilan = fittedByVarsayilan.trim();
  if (yeni && !satir.fittedBy?.trim() && varsayilan) yama.fittedBy = varsayilan;
  if (!listeSatiriMi(satir.sira) && satir.durum === "takildi" && yeni !== "takildi" && satir.birimler.length > 0) yama.birimler = [];
  return yama;
}

/** C&K rolü seçilmemiş: yalnız 3. ve 6. satırda, birimin kimliği doluyken ve rolü boşken (engellemez). */
export function rolUyarisi(sira: number, birim: Partial<Record<BirimAlani, string | null>> & { rol?: string | null }): boolean {
  return ROL_SATIRLARI.includes(sira) && kimlikli(birim) && !birim.rol;
}

/**
 * Parça başına kimlik taşıyan "fitted" satırında girilen birim beklenenden azsa sayım (satırın başında sarı etiket; engellemez).
 * Tamamsa, öteki satırlarda ve öteki durumlarda null.
 */
export function birimEksigi(x: Pick<MontajSatirYaniti, "sira" | "durum" | "qty" | "basiliQty"> & { birimler: readonly Partial<Record<BirimAlani, string | null>>[] }): { girilen: number; beklenen: number } | null {
  if (!PARCA_BASINA.has(x.sira) || x.durum !== "takildi") return null;
  const beklenen = birimSayisi(x.sira, etkinAdet(x));
  const girilen = x.birimler.filter(kimlikli).length;
  return girilen < beklenen ? { girilen, beklenen } : null;
}

/* ---------------------------------------------------------------- ölçüler ---------------------------------------------------------------- */

/** Ölçü girdisinde görünen metin: kesirli girildiyse yazıldığı gibi, değilse ondalık değer; ölçü yoksa boş. */
export function olcuGirdiMetni(o: { deger: number | null; yazim: string | null } | null | undefined): string {
  return o?.yazim?.trim() || (o?.deger != null ? String(o.deger) : "");
}

/**
 * Ölçü girdisinin sonucu: "hata" = okunamadı, istek gitmez (ondalık ya da kesirli inç beklenir; `montajOlcuCoz`) · "ayni" =
 * kayıttakiyle aynı, istek gitmez · "yaz" = gövdeye girecek ölçüm (boş girdi ölçüyü siler). Akla yatkınlık aralığını sunucu denetler.
 */
export function olcuYamasi(
  tip: MontajOlcumTipi,
  hat: MontajOlcumHatti,
  metin: string,
  mevcut: Pick<MontajOlcumYaniti, "deger" | "yazim"> | null | undefined,
): { tur: "hata" } | { tur: "ayni" } | { tur: "yaz"; olcum: OlcumYamasi } {
  const c = montajOlcuCoz(metin);
  if (!c) return { tur: "hata" };
  if (c.deger === (mevcut?.deger ?? null) && c.yazim === (mevcut?.yazim?.trim() || null)) return { tur: "ayni" };
  return { tur: "yaz", olcum: { tip, hat, deger: c.deger, yazim: c.yazim } };
}

/* --------------------------------------------------- doluluk, print-out ve son kutu --------------------------------------------------- */

/**
 * Formun doluluğu: durumu seçilmiş satırlar, değeri girilmiş ölçüler ve Assembly Serial No., hepsi eşit ağırlıkla
 * (30 satır + 8 ölçü + 1 = 39). Yüzde 100 olsa da print-out kapısı kapalı olabilir (takılan satırda kimlik yoksa); kapının
 * kuralı sunucunundur.
 */
export function formYuzdesi(k: Pick<MontajKaydiYaniti, "doluluk" | "olcumler" | "assemblySnYazim">): { yuzde: number; satirDolu: number; satirToplam: number; olcuDolu: number } {
  const satirDolu = k.doluluk.dolu;
  const satirToplam = k.doluluk.toplam;
  const olcuDolu = k.olcumler.filter((o) => o.deger != null).length;
  const asn = k.assemblySnYazim?.trim() ? 1 : 0;
  return { yuzde: Math.round((100 * (satirDolu + olcuDolu + asn)) / (satirToplam + FORM_OLCU_SAYISI + 1)), satirDolu, satirToplam, olcuDolu };
}

/**
 * Kapalı "Generate print-out" düğmesinin ipucu: ilk beş eksik, kalanı sayıyla; eksik yoksa undefined. Eksiklerin adları sunucudan
 * İngilizce gelir ("row 3 status", "stick-out CH") ve aynen yazılır.
 */
export function yazdirmaIpucu(eksikler: readonly string[]): string | undefined {
  if (!eksikler.length) return undefined;
  const kalan = eksikler.length - 5;
  return cevir("Not complete yet. Missing: {0}", eksikler.slice(0, 5).join(", ") + (kalan > 0 ? ` +${kalan}` : ""));
}

/**
 * "Review and approval" kutusunun altında ne çizilir:
 * - `yazdir`: doluluk satırı ve "Generate print-out" (form açık)
 * - `imzali_yukle`: print-out üretildi; imzalı çıktı bekleniyor ("Upload signed print-out")
 * - `kayit_kapat`: imzalı çıktı Finishing'e yüklendi ama kayıt kapanmadı ("Close the record")
 * - `kapali`: kayıt kapalı (imzalı çıktı Finishing'in raporuysa "Remove signed print-out" da çizilir)
 * - `imzali_yeniden`: kapalı kaydın imzalı çıktısı Finishing'den kaldırılmış; yeniden yüklenir
 * - `baska_dosya`: kapalı kayıt, Finishing başka bir dosya tutuyor ("Close the record" kaydı o belgeye bağlar)
 */
export type FormAdimi = "yazdir" | "imzali_yukle" | "kayit_kapat" | "kapali" | "imzali_yeniden" | "baska_dosya";
export function formAdimi(v: { kayit: Pick<MontajKaydiYaniti, "hal">; akis: Pick<MontajAkisBilgisi, "asmRaporu">; raporUyumu: MontajRaporUyumu }): FormAdimi {
  switch (v.kayit.hal) {
    case "form_kapali":
      return v.raporUyumu === "bag_yok" ? "imzali_yeniden" : v.raporUyumu === "farkli" ? "baska_dosya" : "kapali";
    case "imzali_bekliyor":
      return v.akis.asmRaporu ? "kayit_kapat" : "imzali_yukle";
    default:
      return "yazdir";
  }
}

/**
 * Form sayfasının başlık etiketleri: Finishing satırındakiyle aynı söz (`satirEtiketleri`). Tek fark: açık formun raporu yoktur;
 * imzalı çıktı Finishing'e yüklenmiş ama kayıt henüz kapanmamışken ("Close the record" bekleniyor) "record of another file"
 * denmez. O etiket yalnız kapalı kaydın belgesi Finishing'inkinden farklıyken doğrudur.
 */
export function formEtiketleri(v: Pick<MontajRiserYaniti, "akis" | "raporUyumu"> & { kayit: Pick<MontajKaydiYaniti, "hal" | "tur" | "kapali" | "doluluk"> }): EtiketGorunumu[] {
  const k = v.kayit;
  return satirEtiketleri(
    { gonderildi: v.akis.gonderildi, asmVar: Boolean(v.akis.asmRaporu) },
    { hal: k.hal, tur: k.tur, raporUyumu: k.kapali ? v.raporUyumu : "bag_yok", dolu: k.doluluk.dolu, toplam: k.doluluk.toplam },
  );
}

/**
 * Formun üstündeki sarı bant: imzalı çıktı henüz yüklenecekse ve NDT'si eksik hat varsa. Form yine doldurulur; imzalı çıktının
 * yüklenmesi var olan NDT kapısına takılır. İmzalı çıktı yüklüyken ve kapalı kayıtta (yeniden yükleme beklenmiyorsa) null.
 */
export function ndtBandi(v: { kayit: Pick<MontajKaydiYaniti, "hal">; akis: Pick<MontajAkisBilgisi, "asmRaporu" | "ndtEksik">; raporUyumu: MontajRaporUyumu }): string | null {
  if (v.akis.asmRaporu || !v.akis.ndtEksik.length) return null;
  if (v.kayit.hal === "form_kapali" && v.raporUyumu !== "bag_yok") return null;
  return cevir("The signed print-out can be uploaded only after every line has an NDT report or No record available. Missing now: {0}.", v.akis.ndtEksik.join(", "));
}

/* ------------------------------------------------------------- kayıt sırası ------------------------------------------------------------- */

/**
 * Alan kayıtlarını ve düğme işlemlerini sıraya dizer: her iş bir öncekinin bitmesini bekler (düşse de). Böylece bir alanın
 * gövdesi, önceki alanın yanıtıyla gelen durumdan kurulur; hızlı arka arkaya çıkılan iki alan birbirinin yazdığını ezmez.
 */
export function isSirasi() {
  let son: Promise<unknown> = Promise.resolve();
  function ekle<T>(is: () => Promise<T>): Promise<T> {
    const sonuc = son.then(is, is);
    son = sonuc.catch(() => undefined);
    return sonuc;
  }
  return ekle;
}

/* ---------------------------------------------------------- API çağrıları ---------------------------------------------------------- */

/** "Fill the form on screen": riserda ekran formunu başlatır (30 satır ve 8 ölçü boş açılır; hiçbir alan kendiliğinden dolmaz). */
export const montajFormAc = (rn: string) => api.post<{ id: string }>(MONTAJ_FORM_UCU.ac(rn));
/** Alan güncellemesi; yanıt sayfanın tam durumudur. */
export const montajFormGuncelle = (id: string, govde: MontajFormGovdesi) => api.patch<MontajRiserYaniti>(MONTAJ_FORM_UCU.guncelle(id), govde);
/** "Generate print-out": yalnız işaret yazar. */
export const montajFormYazdir = (id: string) => api.post<{ ok: true }>(MONTAJ_FORM_UCU.yazdir(id));
/** Kaydı Finishing'deki imzalı çıktıya bağlar ve kapatır ("Upload signed print-out"un ikinci adımı ve "Close the record"). */
export const montajFormImzala = (id: string) => api.post<{ ok: true; dokumanId: string }>(MONTAJ_FORM_UCU.imzali(id));
